import type * as React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import { FloatingLabelAsyncCombobox } from '@/shared/components/custom/async-combobox/FloatingLabelAsyncCombobox';
import type { FloatingInputVariant } from './FormFieldInput';
import type {
  AsyncComboboxOption,
  AsyncComboboxFetchFn,
} from '@/shared/components/custom/async-combobox/AsyncCombobox.types';

interface FormFieldAsyncComboboxBaseProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  floatingLabel?: boolean;
  floatingVariant?: FloatingInputVariant;
  disabled?: boolean;
  'data-testid'?: string;
  queryKey: readonly unknown[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

interface FormFieldAsyncComboboxSingleProps<
  T extends FieldValues,
  O extends AsyncComboboxOption,
> extends FormFieldAsyncComboboxBaseProps<T> {
  mode?: 'single';
  fetchFn?: AsyncComboboxFetchFn<O>;
  staticOptions?: O[];
  clearable?: boolean;
  selectedOptions?: O[];
  renderOption?: (option: O, selected: boolean) => React.ReactNode;
  renderValue?: (option: O) => React.ReactNode;
}

interface FormFieldAsyncComboboxMultiProps<
  T extends FieldValues,
  O extends AsyncComboboxOption,
> extends FormFieldAsyncComboboxBaseProps<T> {
  mode: 'multi';
  fetchFn?: AsyncComboboxFetchFn<O>;
  staticOptions?: O[];
  maxSelections?: number;
  maxDisplayedBadges?: number;
  selectedOptions?: O[];
  renderOption?: (option: O, selected: boolean) => React.ReactNode;
  renderValue?: (option: O) => React.ReactNode;
}

export type FormFieldAsyncComboboxProps<
  T extends FieldValues,
  O extends AsyncComboboxOption = AsyncComboboxOption,
> = FormFieldAsyncComboboxSingleProps<T, O> | FormFieldAsyncComboboxMultiProps<T, O>;

export function FormFieldAsyncCombobox<
  T extends FieldValues,
  O extends AsyncComboboxOption = AsyncComboboxOption,
>({
  control,
  name,
  label,
  floatingLabel = false,
  floatingVariant = 'default',
  disabled,
  ...rest
}: FormFieldAsyncComboboxProps<T, O>) {
  const isMulti = rest.mode === 'multi';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const value = (field.value ?? (isMulti ? [] : null)) as string | null | string[];

        if (floatingLabel) {
          return (
            <Field data-invalid={fieldState.invalid}>
              {isMulti ? (
                <FloatingLabelAsyncCombobox<O>
                  {...(rest as FormFieldAsyncComboboxMultiProps<T, O>)}
                  id={name}
                  label={label}
                  mode="multi"
                  value={value as string[]}
                  onChange={(v: string[]) => field.onChange(v)}
                  invalid={fieldState.invalid}
                  variant={floatingVariant}
                  disabled={disabled}
                />
              ) : (
                <FloatingLabelAsyncCombobox<O>
                  {...(rest as FormFieldAsyncComboboxSingleProps<T, O>)}
                  id={name}
                  label={label}
                  value={value as string | null}
                  onChange={(v: string | null) => field.onChange(v)}
                  invalid={fieldState.invalid}
                  variant={floatingVariant}
                  disabled={disabled}
                />
              )}
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          );
        }

        return (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor={name}>{label}</FieldLabel>
            {isMulti ? (
              <AsyncCombobox<O>
                {...(rest as FormFieldAsyncComboboxMultiProps<T, O>)}
                mode="multi"
                value={value as string[]}
                onChange={(v: string[]) => field.onChange(v)}
                disabled={disabled}
              />
            ) : (
              <AsyncCombobox<O>
                {...(rest as FormFieldAsyncComboboxSingleProps<T, O>)}
                value={value as string | null}
                onChange={(v: string | null) => field.onChange(v)}
                disabled={disabled}
              />
            )}
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        );
      }}
    />
  );
}
